import { Button, Group, Modal, NumberInput, TextInput } from '@mantine/core';
import { useForm } from '@mantine/form';
import { LifeEvent } from '../hooks/useLifeEvents';
import React, { useEffect } from 'react';
import { currencySymbol } from 'src/common/currency';
import { useCurrency } from 'src/common/hooks/useCurrency';

type LifeEventModalProps = {
  opened: boolean;
  onClose: () => void;
  onSubmit: (lifeEvent: Omit<LifeEvent, 'id' | 'clientId'>) => Promise<void>;
  initialValues?: LifeEvent;
};

export function LifeEventModal({ opened, onClose, onSubmit, initialValues }: LifeEventModalProps) {
  const currency = useCurrency();
  const form = useForm<Omit<LifeEvent, 'id' | 'clientId'>>({
    initialValues: initialValues || {
      description: '',
      cost: 0,
      details: '',
      eventDate: new Date().toISOString().split('T')[0],
    },
    validate: {
      cost: value =>
        typeof value !== 'number' || value < 0 ? 'Amount must be a number (0 or greater)' : null,
    },
    validateInputOnBlur: true,
  });

  // Reset form when modal opens/closes or initialValues changes
  useEffect(() => {
    if (opened) {
      form.setValues(
        initialValues || {
          description: '',
          cost: 0,
          details: '',
          eventDate: new Date().toISOString().split('T')[0],
        },
      );
    }
  }, [opened, initialValues]);

  const handleSubmit = async (values: Omit<LifeEvent, 'id' | 'clientId'>) => {
    try {
      await onSubmit(values);
    } catch {
      // Already reported by the caller; keep what was typed so it can be saved again.
      return;
    }
    form.reset();
  };

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      title={initialValues?.id ? 'Edit life event' : 'Add life event'}
    >
      <form onSubmit={form.onSubmit(handleSubmit)} data-testid="life-events-modal">
        <TextInput label="Description" {...form.getInputProps('description')} required />
        <TextInput label="Date" type="date" {...form.getInputProps('eventDate')} required />
        <TextInput label="Details" {...form.getInputProps('details')} />
        <NumberInput
          label="Amount"
          {...form.getInputProps('cost')}
          required
          leftSection={currencySymbol(currency)}
          hideControls
        />
        <Group justify="flex-end" mt="md">
          <Button variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button type="submit">Save</Button>
        </Group>
      </form>
    </Modal>
  );
}
